let getter: (() => string | null) | null = null
let overrideToken: string | null = null

const ACCESS_TOKEN_SS_KEY = 'mc_access_token'
const TENANT_TOKENS_KEY = 'mc_tenant_access_tokens'

export const setAccessTokenGetter = (fn: () => string | null) => {
  getter = fn
  const tok = fn()
  if (tok)
    try {
      sessionStorage.setItem(ACCESS_TOKEN_SS_KEY, tok)
    } catch {
      /* ignore */
    }
}

export const setAccessTokenOverride = (tok: string | null) => {
  overrideToken = tok
  try {
    if (tok) sessionStorage.setItem(ACCESS_TOKEN_SS_KEY, tok)
    else sessionStorage.removeItem(ACCESS_TOKEN_SS_KEY)
  } catch {
    /* ignore */
  }
}

export const getAccessToken = () =>
  overrideToken ??
  (getter ? getter() : null) ??
  sessionStorage.getItem(ACCESS_TOKEN_SS_KEY)

let renewer: (() => Promise<void>) | null = null

export const setAccessTokenRenewer = (fn: (() => Promise<void>) | null) => {
  renewer = fn
}

// Background tabs throttle the OIDC renew timer, so a request can wake up
// holding a token that lapsed while it slept. Renew first rather than let the
// 401 read as a dead session; a renew that fails still ends in that 401.
export const ensureFreshAccessToken = async () => {
  const token = getAccessToken()
  if (!renewer || !token || !isTokenPastExpiry(token)) return

  try {
    await renewer()
  } catch {
    /* the request's 401 reports it */
  }
}

export const clearAccessTokenCache = () => {
  overrideToken = null
  try {
    sessionStorage.removeItem(ACCESS_TOKEN_SS_KEY)
  } catch {
    /* ignore */
  }
}

export function prehydrateAccessTokenFromOidcStorage() {
  try {
    if (sessionStorage.getItem(ACCESS_TOKEN_SS_KEY)) return
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i) || ''
      // @axa-fr/react-oidc stores tokens under `oidc.<configName>:<redirectUri>`
      // as { tokens: { access_token } }; skip its metadata keys
      if (!k.startsWith('oidc.') && !k.startsWith('user:')) continue
      if (
        k.startsWith('oidc.server:') ||
        k.startsWith('oidc.session_state.') ||
        k.startsWith('oidc.nonce.')
      )
        continue

      const raw = sessionStorage.getItem(k)
      if (!raw) continue
      let obj: any
      try {
        obj = JSON.parse(raw)
      } catch {
        continue
      }

      const tok =
        obj?.tokens?.access_token ||
        obj?.access_token ||
        obj?.accessToken ||
        obj?.user?.access_token ||
        obj?.user?.accessToken ||
        null

      if (typeof tok === 'string' && tok) {
        sessionStorage.setItem(ACCESS_TOKEN_SS_KEY, tok)
        break
      }
    }
  } catch {
    /* ignore */
  }
}

function decodeJwtPayload(token: string): any | null {
  try {
    const base64 = token
      .split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch {
    return null
  }
}

export function decodeTenantIdFromJwt(token: string): string | null {
  const payload = decodeJwtPayload(token)
  const tid = payload?.TenantId ?? payload?.tenantId ?? payload?.tid
  return tid ? String(tid) : null
}

// The caller's own feature/client id, as enforced by the backend's
// client_id === {featureId} check on GET /admin/feature/{featureId}/message.
// Used by the status banner so any valid user (with the read scope) can load
// their feature's messages plus global ones — no BackOffice role required.
export function decodeClientIdFromJwt(token: string): string | null {
  const payload = decodeJwtPayload(token)
  const cid =
    payload?.client_id ?? payload?.azp ?? payload?.appid ?? payload?.clientId
  return cid ? String(cid) : null
}

// Undecodable means "refresh it" for callers deciding whether to renew.
export function isJwtExpired(token: string, skewSeconds = 60): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload) return true

  const expSec = Number(payload?.exp || 0)
  if (!expSec) return false

  return expSec <= Math.floor(Date.now() / 1000) + skewSeconds
}

// Undecodable means "do not blame the session" for callers classifying a 401
// that already happened.
export function isTokenPastExpiry(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload) return false

  const expSec = Number(payload?.exp || 0)
  if (!expSec) return false

  return expSec <= Math.floor(Date.now() / 1000)
}

export function tokenBelongsToTenant(token: string, tenantId: string): boolean {
  return decodeTenantIdFromJwt(token) === String(tenantId)
}

export function isTokenUsableForTenant(
  token: string,
  tenantId: string
): boolean {
  return (
    !!token && tokenBelongsToTenant(token, tenantId) && !isJwtExpired(token)
  )
}

export function cacheTenantAccessToken(tenantId: string, token: string) {
  if (!tenantId || !token) return
  try {
    const map = JSON.parse(localStorage.getItem(TENANT_TOKENS_KEY) || '{}')
    map[tenantId] = token
    localStorage.setItem(TENANT_TOKENS_KEY, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

export function getCachedTenantAccessToken(tenantId: string): string | null {
  try {
    const map = JSON.parse(localStorage.getItem(TENANT_TOKENS_KEY) || '{}')
    return map?.[tenantId] || null
  } catch {
    return null
  }
}

export function clearTenantAccessToken(tenantId: string) {
  try {
    const map = JSON.parse(localStorage.getItem(TENANT_TOKENS_KEY) || '{}')
    if (map?.[tenantId]) {
      delete map[tenantId]
      localStorage.setItem(TENANT_TOKENS_KEY, JSON.stringify(map))
    }
  } catch {
    /* ignore */
  }
}
